import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseData, useDatabaseItem } from "@/components/page/hooks"
import { deleteTeam } from "@/lib/teams"
import { Combobox } from "@/components/ui/combobox"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2, Users } from "lucide-react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { supabase } from "@/lib/data"

export const teams_crud = {
  title: "Team",
  route: "/settings/teams/:id",
  component,
  resource: "teams",
  icon: Users,
  action: "read",
} satisfies PageConfig

const FormSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  owner: z.string().optional().nullable(),
  technical_owner: z.string().optional().nullable(),
})

type FormData = z.infer<typeof FormSchema>

export function component() {
  const { data, loading, submit, toastSuccess, navigate, id } = useDatabaseItem("teams")
  const { data: users, loading: usersLoading } = useDatabaseData("users")

  // const userOptions = useMemo(() => {
  //   return users.map((user) => ({
  //     value: user.id,
  //     label: [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || user.id,
  //   }))
  // }, [users])

  const userOptions = users.map((user) => ({
    value: user.id,
    label: [user.first_name, user.last_name].join(" ") || user.email || user.id,
  }))

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data,
    values: data,
  })

  async function onSubmit(data: FormData) {
    console.log("Submit: ", data)
    await submit(data)
    toastSuccess()
    navigate("/settings/teams")
  }

  const [confirmName, setConfirmName] = useState("")
  const [resetting, setResetting] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)

  async function resetContent() {
    setResetting(true)
    try {

      if (id) {
        const { data: agents } = await supabase.from("agents").select("id").eq("team_id", id)

        await supabase.from("events").delete().eq("team", id)

        await supabase.from("history").delete().eq("team", id)
        await supabase.from("messages").delete().eq("team", id)
        await supabase.from('contacts').delete().eq('team', id)

        for (const agent of agents || []) {
          await supabase.from("history").delete().eq("agent", `${agent.id}`)
          await supabase.from("messages").delete().eq("agent", agent.id)
        }
      }
      toast.success("All content has been deleted")
      setResetOpen(false)
    } finally {
      setResetting(false)
    }
  }

  if (loading || usersLoading) return <CRUDLoader />

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="teams" onSuccess={"/settings/teams"} onDelete={async () => { await deleteTeam(id) }} />
          </PageTitle>

          <div className="rgrid">
            <div className="row">
              <div className="rc-12">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12">
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl><Textarea {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="owner" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner</FormLabel>
                    <FormControl>
                      <Combobox
                        items={userOptions}
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="Search users..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="technical_owner" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Technical Support</FormLabel>
                    <FormControl>
                      <Combobox
                        items={userOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Search users..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
          </div>
        </form>
      </Form>

      <Alert variant="destructive" className="mt-12">
        <AlertTitle className="text-base">Danger zone</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>Reset all content for this team. This action cannot be undone.</span>
          <AlertDialog open={resetOpen} onOpenChange={(open) => { if (!resetting) { setResetOpen(open); setConfirmName("") } }}>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="destructive">Reset Content</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reset content for "{data.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all chats, contacts and states for this team. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-2">
                <Label>Type <span className="font-semibold">{data.name}</span> to confirm</Label>
                <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={data.name} disabled={resetting} />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={confirmName !== data.name || resetting}
                  className="bg-[linear-gradient(135deg,#EA5850,#9B53D2)] text-white hover:opacity-90"
                  onClick={(e) => { e.preventDefault(); resetContent() }}
                >
                  {resetting && <Loader2 className="animate-spin" />}
                  {resetting ? "Deleting..." : "Reset Content"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </AlertDescription>
      </Alert>
    </div>
  </Page>
}
